import { useEffect, useRef } from 'react';

/**
 * LiveTransform — applies dramatic real-time visual effects to the live camera.
 * GPU-accelerated via CSS filters + selective canvas pixel processing.
 * Built to feel like a Snapchat lens but free of any heavy ML dependency.
 *
 * Effects:
 *  - cssFilter: a `filter:` string applied on the <video> tag (instant, GPU)
 *  - mode 'pixel': downsample then upsample → chunky pixel art
 *  - mode 'comic': posterize colours + boost contrast for a comic-book look
 *  - mode 'sketch': edge-detection via Sobel-ish high-pass + grayscale
 */

const PIXEL_SIZE = 14;
const FPS_CAP = 30;
const FRAME_INTERVAL = 1000 / FPS_CAP;

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export default function LiveTransform({ videoRef, look, active }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(0);

  useEffect(() => {
    if (!active || !look?.mode) {
      cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return undefined;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    const maxDim = isMobile() ? 480 : 640;

    const drawDownscaled = () => {
      const vw = v.videoWidth, vh = v.videoHeight;
      const r = Math.min(1, maxDim / Math.max(vw, vh));
      const w = Math.round(vw * r);
      const h = Math.round(vh * r);
      if (c.width !== w) c.width = w;
      if (c.height !== h) c.height = h;
      ctx.save();
      ctx.scale(-1, 1);
      ctx.translate(-w, 0);
      ctx.imageSmoothingEnabled = look.mode !== 'pixel';
      ctx.drawImage(v, 0, 0, w, h);
      ctx.restore();
      return { w, h };
    };

    const applyPixel = (w, h) => {
      const sx = Math.max(1, Math.floor(w / PIXEL_SIZE));
      const sy = Math.max(1, Math.floor(h / PIXEL_SIZE));
      const tmp = document.createElement('canvas');
      tmp.width = sx; tmp.height = sy;
      const tctx = tmp.getContext('2d');
      tctx.drawImage(c, 0, 0, sx, sy);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(tmp, 0, 0, sx, sy, 0, 0, w, h);
    };

    const applyComic = (w, h) => {
      const img = ctx.getImageData(0, 0, w, h);
      const d = img.data;
      const levels = 4; // posterize
      const step = 255 / (levels - 1);
      for (let i = 0; i < d.length; i += 4) {
        d[i]     = Math.round(d[i]     / step) * step;
        d[i + 1] = Math.round(d[i + 1] / step) * step;
        d[i + 2] = Math.round(d[i + 2] / step) * step;
      }
      ctx.putImageData(img, 0, 0);
    };

    const applySketch = (w, h) => {
      const img = ctx.getImageData(0, 0, w, h);
      const src = img.data;
      // Convert to grayscale + simple edge magnitude (forward differences)
      const out = ctx.createImageData(w, h);
      const dst = out.data;
      for (let y = 0; y < h - 1; y++) {
        for (let x = 0; x < w - 1; x++) {
          const i = (y * w + x) * 4;
          const i2 = (y * w + (x + 1)) * 4;
          const i3 = ((y + 1) * w + x) * 4;
          const g1 = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2];
          const g2 = 0.299 * src[i2] + 0.587 * src[i2 + 1] + 0.114 * src[i2 + 2];
          const g3 = 0.299 * src[i3] + 0.587 * src[i3 + 1] + 0.114 * src[i3 + 2];
          const edge = Math.min(255, Math.abs(g1 - g2) * 6 + Math.abs(g1 - g3) * 6);
          const v2 = 255 - edge;
          dst[i] = dst[i + 1] = dst[i + 2] = v2;
          dst[i + 3] = 255;
        }
      }
      ctx.putImageData(out, 0, 0);
    };

    const render = (ts) => {
      if (ts - lastFrameRef.current < FRAME_INTERVAL) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      lastFrameRef.current = ts;
      if (v.readyState >= 2 && v.videoWidth) {
        const { w, h } = drawDownscaled();
        if (look.mode === 'pixel') applyPixel(w, h);
        else if (look.mode === 'comic') applyComic(w, h);
        else if (look.mode === 'sketch') applySketch(w, h);
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, look, videoRef]);

  if (!active || !look?.mode) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ objectFit: 'cover' }}
      data-testid="live-transform-canvas"
    />
  );
}

// ----- LOOK PRESETS -----
// `cssFilter` = applied directly to the <video> (zero CPU cost).
// `mode`      = enables the canvas overlay for pixel/comic/sketch.
export const LIVE_LOOKS = [
  { id: 'none',      name: 'Aucun',       emoji: '⚪', cssFilter: 'none' },
  { id: 'neon',      name: 'Néon',        emoji: '💜', cssFilter: 'hue-rotate(290deg) saturate(2) brightness(1.1) contrast(1.1)' },
  { id: 'cyberpunk', name: 'Cyberpunk',   emoji: '🤖', cssFilter: 'hue-rotate(200deg) saturate(2.5) contrast(1.4) brightness(0.95)' },
  { id: 'thermal',   name: 'Infrarouge',  emoji: '🔥', cssFilter: 'hue-rotate(40deg) saturate(3) contrast(1.5) brightness(1.1)' },
  { id: 'noir',      name: 'Noir',        emoji: '🎬', cssFilter: 'grayscale(1) contrast(1.6) brightness(0.95)' },
  { id: 'comic',     name: 'Comic',       emoji: '💥', cssFilter: 'saturate(1.6) contrast(1.4)', mode: 'comic' },
  { id: 'sketch',    name: 'Dessin',      emoji: '✏️', cssFilter: 'brightness(1.2)', mode: 'sketch' },
  { id: 'pixel',     name: 'Pixel art',   emoji: '👾', cssFilter: 'saturate(1.5)', mode: 'pixel' },
  { id: 'hologram',  name: 'Hologramme',  emoji: '🔮', cssFilter: 'hue-rotate(160deg) saturate(2) brightness(1.3) drop-shadow(0 0 12px #00e5ff)' },
  { id: 'vintage',   name: 'Vintage',     emoji: '📺', cssFilter: 'sepia(0.7) contrast(1.1) brightness(0.95) hue-rotate(-10deg)' },
];
